import React, { useEffect, useRef, useState } from "react";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsOfService from "./TermsOfService";

// Seasonal theme matching the app's themes.js
const SEASONS = {
  spring: {
    months: [2, 3, 4],
    primary: "#E8679A", primaryLight: "#F48FB1", primaryPale: "#FCE4EC",
    green: "#66BB6A", greenPale: "#E8F5E9",
    navy: "#4A2040", navyLight: "#8E6C88",
    text: "#2D1B28", textMuted: "#9E7A90",
    border: "#F0DDE8", bg: "#FDF6F9",
    heroGrad: "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(232,103,154,0.12) 0%, transparent 70%), radial-gradient(ellipse 60% 40% at 80% 80%, rgba(102,187,106,0.08) 0%, transparent 60%)",
    sidebarBg: "#4A2040",
  },
  summer: {
    months: [5, 6, 7],
    primary: "#E8852E", primaryLight: "#F5A623", primaryPale: "#FFF0DB",
    green: "#4CAF7D", greenPale: "#E3F5EC",
    navy: "#3D3226", navyLight: "#795548",
    text: "#2C2416", textMuted: "#8B7E6A",
    border: "#EDE3D3", bg: "#FFF9F0",
    heroGrad: "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(232,133,46,0.12) 0%, transparent 70%), radial-gradient(ellipse 60% 40% at 80% 80%, rgba(76,175,125,0.08) 0%, transparent 60%)",
    sidebarBg: "#3D3226",
  },
  autumn: {
    months: [8, 9, 10],
    primary: "#BF5B21", primaryLight: "#D4762C", primaryPale: "#FAEBD7",
    green: "#8B6914", greenPale: "#FFF8E1",
    navy: "#3E2723", navyLight: "#6D4C41",
    text: "#2C1B0E", textMuted: "#8D7B6A",
    border: "#E8DDD0", bg: "#FBF5EE",
    heroGrad: "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(191,91,33,0.12) 0%, transparent 70%), radial-gradient(ellipse 60% 40% at 80% 80%, rgba(139,105,20,0.08) 0%, transparent 60%)",
    sidebarBg: "#3E2723",
  },
  winter: {
    months: [11, 0, 1],
    primary: "#3B82C4", primaryLight: "#5BA3E0", primaryPale: "#DBEAFE",
    green: "#2E9E6B", greenPale: "#D1FAE5",
    navy: "#1E293B", navyLight: "#475569",
    text: "#1A2030", textMuted: "#6B7B90",
    border: "#D8E2EE", bg: "#F4F7FB",
    heroGrad: "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(59,130,196,0.12) 0%, transparent 70%), radial-gradient(ellipse 60% 40% at 80% 80%, rgba(46,158,107,0.08) 0%, transparent 60%)",
    sidebarBg: "#1E293B",
  },
};

function getSeason() {
  const month = new Date().getMonth();
  return Object.entries(SEASONS).find(([, s]) => s.months.includes(month))?.[0] || "spring";
}

const LandingPage = ({ onSignIn, onSignUp }) => {
  const T = SEASONS[getSeason()];
  const revealRefs = useRef([]);
  const [legalPage,      setLegalPage]      = useState(null);
  const [showWaitlist,   setShowWaitlist]   = useState(false);
  const [waitlistEmail,  setWaitlistEmail]  = useState("");
  const [waitlistSchool, setWaitlistSchool] = useState("");
  const [waitlistStatus, setWaitlistStatus] = useState("idle");

  const handleWaitlistSubmit = async () => {
    if (!waitlistEmail.trim() || !waitlistSchool.trim()) return;
    setWaitlistStatus("loading");
    try {
      const { supabase } = await import("../supabaseClient");
      const { error } = await supabase.from("waitlist").insert({
        email: waitlistEmail.trim().toLowerCase(),
        school_name: waitlistSchool.trim(),
      });
      if (error && error.code !== "23505") throw error;
      setWaitlistStatus("success");
    } catch (e) {
      console.error(e);
      setWaitlistStatus("error");
    }
  };

  useEffect(() => {
    if (legalPage) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(el => {
        if (el.isIntersecting) el.target.style.opacity = "1", el.target.style.transform = "translateY(0)";
      });
    }, { threshold: 0.1 });
    revealRefs.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, [legalPage]);

  if (legalPage === "privacy") return <PrivacyPolicy onBack={() => setLegalPage(null)} />;
  if (legalPage === "terms") return <TermsOfService onBack={() => setLegalPage(null)} />;

  const reveal = (i = 0) => ({
    ref: el => revealRefs.current.push(el),
    style: {
      opacity: 0, transform: "translateY(24px)",
      transition: `opacity 0.6s ease ${i * 0.1}s, transform 0.6s ease ${i * 0.1}s`
    }
  });

  // SVG Icons
  const IconList = () => <svg width="24" height="24" viewBox="0 0 256 256" fill="currentColor"><path d="M32 56a8 8 0 0 1 8-8h176a8 8 0 0 1 0 16H40a8 8 0 0 1-8-8zm8 80h176a8 8 0 0 0 0-16H40a8 8 0 0 0 0 16zm176 48H40a8 8 0 0 0 0 16h176a8 8 0 0 0 0-16z"/></svg>;
  const IconUser = () => <svg width="24" height="24" viewBox="0 0 256 256" fill="currentColor"><path d="M117.25 157.92a60 60 0 1 0-66.5 0A95.83 95.83 0 0 0 24.76 174C16 183.74 16 190.89 16 192a8 8 0 0 0 16 0c0-4.29 7.06-17 22.39-26.32a76 76 0 0 1 147.22 0C217 175 224 187.71 224 192a8 8 0 0 0 16 0c0-1.11 0-8.26-8.76-18.08a95.83 95.83 0 0 0-25.99-16.08zM84 112a44 44 0 1 1 44 44 44.05 44.05 0 0 1-44-44z"/></svg>;
  const IconChat = () => <svg width="24" height="24" viewBox="0 0 256 256" fill="currentColor"><path d="M216 48H40a16 16 0 0 0-16 16v160a15.85 15.85 0 0 0 9.24 14.5A16.13 16.13 0 0 0 40 240a15.89 15.89 0 0 0 10.25-3.78.69.69 0 0 0 .13-.11L82.5 208H216a16 16 0 0 0 16-16V64a16 16 0 0 0-16-16zM40 224zm176-32H82.5a16 16 0 0 0-10.3 3.75l-.12.11L40 224V64h176z"/></svg>;
  const IconCard = () => <svg width="24" height="24" viewBox="0 0 256 256" fill="currentColor"><path d="M224 80H32A16 16 0 0 0 16 96v128a16 16 0 0 0 16 16h192a16 16 0 0 0 16-16V96a16 16 0 0 0-16-16zm0 144H32V96h192zM208 40a8 8 0 0 1-8 8H56a8 8 0 0 1 0-16h144a8 8 0 0 1 8 8zm-32-32a8 8 0 0 1-8 8H88a8 8 0 0 1 0-16h80a8 8 0 0 1 8 8z"/></svg>;
  const IconChart = () => <svg width="24" height="24" viewBox="0 0 256 256" fill="currentColor"><path d="M232 208h-8V104a16 16 0 0 0-16-16h-40V64a16 16 0 0 0-16-16H104a16 16 0 0 0-16 16v24H48a16 16 0 0 0-16 16v104h-8a8 8 0 0 0 0 16h208a8 8 0 0 0 0-16zM104 64h48v24h-48zm-56 40h160v104H48z"/></svg>;
  const IconLock = () => <svg width="24" height="24" viewBox="0 0 256 256" fill="currentColor"><path d="M208 80h-32V56a48 48 0 0 0-96 0v24H48a16 16 0 0 0-16 16v128a16 16 0 0 0 16 16h160a16 16 0 0 0 16-16V96a16 16 0 0 0-16-16zM96 56a32 32 0 0 1 64 0v24H96zm112 168H48V96h160zm-80-96a32 32 0 1 0 32 32 32 32 0 0 0-32-32zm0 48a16 16 0 1 1 16-16 16 16 0 0 1-16 16z"/></svg>;

  const s = {
    page: { fontFamily: "'DM Sans', -apple-system, sans-serif", background: T.bg, color: T.text, overflowX: "hidden" },
    // Nav
    nav: { position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 5%", height: 68, background: `${T.bg}ee`, backdropFilter: "blur(12px)", borderBottom: `1px solid ${T.border}` },
    navLogo: { display: "flex", alignItems: "center", gap: 10, fontFamily: "'Nunito', sans-serif", fontWeight: 900, fontSize: 20, color: T.navy, cursor: "pointer", textDecoration: "none" },
    navLogoIcon: { width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${T.primary}, ${T.primaryLight})`, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 900, fontSize: 16, fontFamily: "'Nunito', sans-serif" },
    navLinks: { display: "flex", alignItems: "center", gap: 32 },
    navLink: { fontSize: 14, fontWeight: 600, color: T.navyLight, textDecoration: "none", cursor: "pointer", background: "none", border: "none", fontFamily: "inherit" },
    btnGhost: { fontSize: 14, fontWeight: 700, color: T.navy, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", padding: "8px 16px", borderRadius: 8 },
    btnPrimary: { fontSize: 14, fontWeight: 700, color: "white", background: T.primary, border: "none", cursor: "pointer", fontFamily: "inherit", padding: "10px 20px", borderRadius: 10, transition: "all 0.2s" },
    // Hero
    hero: { minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "100px 5% 80px", position: "relative", overflow: "hidden" },
    heroBg: { position: "absolute", inset: 0, background: T.heroGrad },
    badge: { display: "inline-flex", alignItems: "center", gap: 8, background: T.primaryPale, border: `1px solid ${T.primary}44`, borderRadius: 100, padding: "6px 16px", fontSize: 13, fontWeight: 700, color: T.primary, marginBottom: 28 },
    badgeDot: { width: 6, height: 6, borderRadius: "50%", background: T.green },
    h1: { fontFamily: "'Nunito', sans-serif", fontWeight: 900, fontSize: "clamp(40px, 6vw, 76px)", lineHeight: 1.1, color: T.navy, maxWidth: 820, marginBottom: 24 },
    heroSub: { fontSize: "clamp(16px, 2vw, 19px)", color: T.textMuted, maxWidth: 540, lineHeight: 1.7, marginBottom: 40 },
    heroActions: { display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", justifyContent: "center" },
    btnHero: { fontSize: 16, fontWeight: 800, color: "white", background: `linear-gradient(135deg, ${T.primary}, ${T.primaryLight})`, border: "none", cursor: "pointer", fontFamily: "inherit", padding: "16px 36px", borderRadius: 14, boxShadow: `0 8px 32px ${T.primary}44`, transition: "all 0.2s" },
    btnHeroGhost: { fontSize: 15, fontWeight: 700, color: T.navy, background: "white", border: `2px solid ${T.border}`, cursor: "pointer", fontFamily: "inherit", padding: "14px 32px", borderRadius: 14, transition: "all 0.2s" },
    heroNote: { marginTop: 16, fontSize: 13, color: T.textMuted },
    // Screenshot
    screenshotWrap: { marginTop: 64, width: "100%", maxWidth: 900, position: "relative", zIndex: 1 },
    frame: { background: "white", borderRadius: 20, boxShadow: `0 32px 80px ${T.navy}1e, 0 0 0 1px ${T.border}`, overflow: "hidden" },
    frameBar: { background: T.sidebarBg, padding: "12px 20px", display: "flex", alignItems: "center", gap: 8 },
    dot: (c) => ({ width: 10, height: 10, borderRadius: "50%", background: c }),
    frameUrl: { marginLeft: 16, background: "rgba(255,255,255,0.1)", borderRadius: 6, padding: "4px 16px", fontSize: 12, color: "rgba(255,255,255,0.6)", fontFamily: "monospace" },
    frameContent: { display: "grid", gridTemplateColumns: "200px 1fr", minHeight: 380 },
    sidebar: { background: T.sidebarBg, padding: "24px 16px", display: "flex", flexDirection: "column", gap: 4 },
    sidebarLogo: { fontFamily: "'Nunito', sans-serif", fontWeight: 900, fontSize: 13, color: "white", padding: "8px 12px", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 },
    sidebarLogoDot: { width: 24, height: 24, borderRadius: 7, background: `linear-gradient(135deg, ${T.primary}, ${T.primaryLight})` },
    sidebarItem: (active) => ({ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, color: active ? T.primaryLight : "rgba(255,255,255,0.45)", background: active ? `${T.primary}33` : "transparent" }),
    sidebarDot: { width: 8, height: 8, borderRadius: 2, background: "currentColor", flexShrink: 0 },
    main: { background: T.bg, padding: 24, display: "flex", flexDirection: "column", gap: 16 },
    mainHeader: { fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 17, color: T.navy },
    statsGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 },
    statCard: { background: "white", borderRadius: 12, padding: 16, border: `1px solid ${T.border}` },
    statLabel: { fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" },
    statValue: { fontFamily: "'Nunito', sans-serif", fontWeight: 900, fontSize: 26, color: T.navy, marginTop: 4 },
    statSub: { fontSize: 10, color: T.textMuted, marginTop: 2 },
    badge2: (c, bg) => ({ display: "inline-block", padding: "2px 8px", borderRadius: 100, fontSize: 10, fontWeight: 700, color: c, background: bg, marginTop: 4 }),
    // Sections
    section: (bg = T.bg) => ({ padding: "96px 5%", background: bg }),
    sectionLabel: { display: "inline-block", fontSize: 12, fontWeight: 800, letterSpacing: "1.5px", textTransform: "uppercase", color: T.primary, marginBottom: 16 },
    sectionTitle: { fontFamily: "'Nunito', sans-serif", fontWeight: 900, fontSize: "clamp(28px, 4vw, 46px)", color: T.navy, lineHeight: 1.15, maxWidth: 580, marginBottom: 16 },
    sectionSub: { fontSize: 17, color: T.textMuted, lineHeight: 1.7, maxWidth: 500, marginBottom: 52 },
    featuresGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 },
    featureCard: { background: "white", borderRadius: 20, padding: 32, border: `1px solid ${T.border}`, transition: "all 0.3s" },
    featureIcon: (bg) => ({ width: 52, height: 52, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", background: bg, color: T.primary, marginBottom: 20 }),
    featureTitle: { fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 17, color: T.navy, marginBottom: 10 },
    featureDesc: { fontSize: 14, color: T.textMuted, lineHeight: 1.7 },
    // Steps
    stepsGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 40, marginTop: 52 },
    step: { textAlign: "center" },
    stepNum: { width: 52, height: 52, borderRadius: "50%", background: `linear-gradient(135deg, ${T.primary}, ${T.primaryLight})`, color: "white", fontFamily: "'Nunito', sans-serif", fontWeight: 900, fontSize: 20, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: `0 8px 24px ${T.primary}44` },
    stepTitle: { fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 17, color: T.navy, marginBottom: 10 },
    stepDesc: { fontSize: 14, color: T.textMuted, lineHeight: 1.7 },
    // Pricing
    pricingCard: { maxWidth: 480, margin: "52px auto 0", background: "white", borderRadius: 24, padding: 48, border: `2px solid ${T.primaryLight}`, boxShadow: `0 24px 64px ${T.primary}1e`, textAlign: "center", position: "relative", overflow: "hidden" },
    pricingBar: { position: "absolute", top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${T.primary}, ${T.green})` },
    pricingBadge: { display: "inline-block", background: T.primaryPale, color: T.primary, fontSize: 12, fontWeight: 800, padding: "4px 14px", borderRadius: 100, marginBottom: 20, textTransform: "uppercase", letterSpacing: "0.5px" },
    pricingName: { fontFamily: "'Nunito', sans-serif", fontWeight: 900, fontSize: 26, color: T.navy, marginBottom: 8 },
    pricingPrice: { fontFamily: "'Nunito', sans-serif", fontWeight: 900, fontSize: 60, color: T.navy, lineHeight: 1, margin: "24px 0 8px" },
    pricingNote: { fontSize: 14, color: T.textMuted, marginBottom: 32 },
    featureList: { listStyle: "none", padding: 0, textAlign: "left", marginBottom: 36 },
    featureItem: { display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${T.border}`, fontSize: 15, color: T.text },
    check: { width: 22, height: 22, borderRadius: "50%", background: T.greenPale, color: T.green, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, flexShrink: 0, fontWeight: 800 },
    // Testimonials
    testiGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24, marginTop: 52 },
    testiCard: { background: "rgba(255,255,255,0.06)", borderRadius: 20, padding: 32, border: "1px solid rgba(255,255,255,0.1)" },
    testiStars: { color: "#FFD700", fontSize: 14, marginBottom: 16 },
    testiText: { fontSize: 15, color: "rgba(255,255,255,0.8)", lineHeight: 1.7, marginBottom: 24, fontStyle: "italic" },
    testiAuthor: { display: "flex", alignItems: "center", gap: 12 },
    testiAvatar: (bg) => ({ width: 40, height: 40, borderRadius: "50%", background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 13, color: "white", flexShrink: 0 }),
    // FAQ
    faqGrid: { maxWidth: 720, margin: "52px auto 0" },
    faqItem: { borderBottom: `1px solid ${T.border}`, padding: "24px 0" },
    faqQ: { fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 17, color: T.navy, marginBottom: 12 },
    faqA: { fontSize: 15, color: T.textMuted, lineHeight: 1.7 },
    // CTA
    cta: { padding: "96px 5%", textAlign: "center", background: `linear-gradient(135deg, ${T.primaryPale}, ${T.greenPale})`, borderTop: `1px solid ${T.border}` },
    // Footer
    footer: { background: T.navy, padding: "48px 5% 32px" },
    footerTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 40, flexWrap: "wrap", gap: 32 },
    footerBottom: { borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 24, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 },
    footerText: { fontSize: 13, color: "rgba(255,255,255,0.3)" },
    footerLink: { display: "block", fontSize: 14, color: "rgba(255,255,255,0.6)", textDecoration: "none", marginBottom: 10, cursor: "pointer", background: "none", border: "none", fontFamily: "inherit", textAlign: "left" },
    footerHead: { fontSize: 12, fontWeight: 800, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 16 },
  };

  const features = [
    { icon: <IconList />, bg: T.primaryPale, title: "Attendance & Daily Log", desc: "Track student check-ins, meals, naps, diaper changes, activities, and milestones. Parents see everything in real time." },
    { icon: <IconUser />, bg: T.greenPale, title: "Staff & Coverage", desc: "Manage teacher assignments, track callouts, and organize substitute coverage. Never get caught short-staffed again." },
    { icon: <IconChat />, bg: "#E3F2FD", title: "Parent Messaging", desc: "Direct messaging with parents, classroom announcements, and a full parent portal — keeping families in the loop." },
    { icon: <IconCard />, bg: "#FFF3E0", title: "Billing & Tuition", desc: "Set up tuition plans, track payments, and manage enrollment. Integrated with Stripe for easy online payments." },
    { icon: <IconChart />, bg: "#F3E5F5", title: "Reports & Insights", desc: "Attendance trends, enrollment reports, and daily activity summaries — the data you need to run your school confidently." },
    { icon: <IconLock />, bg: T.greenPale, title: "Secure by Default", desc: "Role-based access for admins, teachers, and parents. Every school's data is fully isolated and protected." },
  ];

  const pricingFeatures = ["Unlimited classrooms & students", "Attendance & daily activity logs", "Staff management & coverage", "Parent portal & messaging", "Billing & tuition tracking", "Reports & data exports", "Enrollment management", "Audit log & security"];

  const testimonials = [
    { text: "We switched from a spreadsheet nightmare to ShrewCare in a weekend. Our teachers actually love using it — and that says everything.", name: "Sarah R.", role: "Director, Sunshine Academy", initials: "SR", grad: `linear-gradient(135deg, ${T.primary}, ${T.primaryLight})` },
    { text: "The parent messaging alone was worth it. No more phone tag, no more missing notes. Parents are more engaged than ever.", name: "Maria C.", role: "Owner, Little Sprouts Learning", initials: "MC", grad: `linear-gradient(135deg, ${T.green}, #81C784)` },
    { text: "The coverage feature saved us twice last month. I can see exactly who's out and who can cover in seconds. Game changer.", name: "James P.", role: "Administrator, Bright Minds Center", initials: "JP", grad: "linear-gradient(135deg, #9C27B0, #CE93D8)" },
  ];

  const faqs = [
    { q: "Is a credit card required to start?", a: "Yes — a credit card is required to begin your 30-day free trial. This ensures uninterrupted access to your school's data. You won't be charged until the trial ends, and you can cancel anytime before then." },
    { q: "How many students and staff can I add?", a: "Unlimited. There are no per-user or per-student fees. One flat price covers your entire school regardless of size." },
    { q: "Can parents see their child's daily log?", a: "Yes. Parents get their own secure portal where they can view attendance, daily activity logs, messages, and upcoming events for their child." },
    { q: "Is my school's data secure?", a: "Absolutely. Each school's data is completely isolated. We use row-level security on every table and role-based access control so staff only see what they're supposed to." },
    { q: "Can I cancel at any time?", a: "Yes, cancel anytime with no penalty. You'll retain access through the end of your billing period." },
    { q: "Does it work on mobile?", a: "Yes. ShrewCare is fully responsive and works great on phones and tablets — perfect for teachers taking attendance from the classroom." },
  ];

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* NAV */}
      <nav style={s.nav}>
        <div style={s.navLogo}>
          <div style={s.navLogoIcon}>S</div>
          ShrewCare
        </div>
        <div style={s.navLinks}>
          {["Features", "How it works", "Pricing", "FAQ"].map(l => (
            <button key={l} style={s.navLink} onClick={() => document.getElementById(l.toLowerCase().replaceAll(" ", "-"))?.scrollIntoView({ behavior: "smooth" })}>{l}</button>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button style={s.btnGhost} onClick={onSignIn}>Sign In</button>
          <button style={s.btnPrimary} onClick={()=>setShowWaitlist(true)}>Request Early Access</button>
        </div>
      </nav>

      {/* HERO */}
      <section style={s.hero}>
        <div style={s.heroBg} />
        <div {...reveal(0)} style={{ ...reveal(0).style, display: "inline-flex", alignItems: "center", gap: 8, background: T.primaryPale, border: `1px solid ${T.primary}44`, borderRadius: 100, padding: "6px 16px", fontSize: 13, fontWeight: 700, color: T.primary, marginBottom: 28 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.green }} />
          Now in early access · Limited spots available
        </div>
        <h1 {...reveal(1)} style={s.h1}>
          The <span style={{ color: T.primary }}>smarter</span> way to run your school
        </h1>
        <p {...reveal(2)} style={s.heroSub}>
          ShrewCare brings attendance, daily logs, billing, parent communication, and staffing together in one beautifully simple platform built for early education.
        </p>
        <div {...reveal(3)} style={s.heroActions}>
          <button style={s.btnHero} onClick={()=>setShowWaitlist(true)}>Request Early Access →</button>
          <button style={s.btnHeroGhost} onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}>See how it works</button>
        </div>
        <p {...reveal(4)} style={s.heroNote}>
          <span style={{ color: T.green, fontWeight: 700 }}>Early access</span> · No credit card required to join the waitlist
        </p>

        {/* Mock Dashboard Screenshot */}
        <div {...reveal(5)} style={s.screenshotWrap}>
          <div style={s.frame}>
            <div style={s.frameBar}>
              <div style={s.dot("#FF5F57")} /><div style={s.dot("#FFBD2E")} /><div style={s.dot("#28C840")} />
              <div style={s.frameUrl}>shrewcare.com/dashboard</div>
            </div>
            <div style={s.frameContent}>
              <div style={s.sidebar}>
                <div style={s.sidebarLogo}><div style={s.sidebarLogoDot} />Sunshine Academy</div>
                {["Dashboard", "Classrooms", "Students", "Attendance", "Daily Log", "Teachers", "Billing", "Messages"].map((item, i) => (
                  <div key={item} style={s.sidebarItem(i === 0)}>
                    <div style={s.sidebarDot} />{item}
                  </div>
                ))}
              </div>
              <div style={s.main}>
                <div style={s.mainHeader}>Good morning, Sarah! Here's today at Sunshine Academy</div>
                <div style={s.statsGrid}>
                  {[
                    { label: "Students Present", value: "42", sub: "of 48 enrolled", badge: "87.5%", bc: T.green, bbg: T.greenPale },
                    { label: "Classrooms", value: "6", sub: "all staffed", badge: "Full", bc: T.green, bbg: T.greenPale },
                    { label: "Open Tickets", value: "3", sub: "2 new today", badge: "Review", bc: T.primary, bbg: T.primaryPale },
                    { label: "Messages", value: "7", sub: "unread", badge: "New", bc: T.primary, bbg: T.primaryPale },
                  ].map(c => (
                    <div key={c.label} style={s.statCard}>
                      <div style={s.statLabel}>{c.label}</div>
                      <div style={s.statValue}>{c.value}</div>
                      <div style={s.statSub}>{c.sub}</div>
                      <span style={s.badge2(c.bc, c.bbg)}>{c.badge}</span>
                    </div>
                  ))}
                </div>
                <div style={{ background: "white", borderRadius: 12, border: `1px solid ${T.border}`, overflow: "hidden" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 80px", padding: "10px 16px", background: T.bg, fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: `1px solid ${T.border}` }}>
                    <span>Student</span><span>Classroom</span><span>Guardian</span><span>Status</span>
                  </div>
                  {[
                    { name: "Emma Rodriguez", room: "Sunflowers", guardian: "Maria R.", present: true },
                    { name: "Liam Chen", room: "Butterflies", guardian: "Wei C.", present: true },
                    { name: "Sofia Patel", room: "Rainbows", guardian: "Priya P.", present: false },
                  ].map((r, i) => (
                    <div key={r.name} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 80px", padding: "10px 16px", borderBottom: i < 2 ? `1px solid ${T.border}` : "none", fontSize: 12, alignItems: "center" }}>
                      <span style={{ fontWeight: 600, color: T.navy }}>{r.name}</span>
                      <span style={{ color: T.textMuted }}>{r.room}</span>
                      <span style={{ color: T.textMuted }}>{r.guardian}</span>
                      <span style={s.badge2(r.present ? T.green : T.primary, r.present ? T.greenPale : T.primaryPale)}>{r.present ? "Present" : "Absent"}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" style={s.section("white")}>
        <div style={{ textAlign: "center" }}>
          <div {...reveal()} style={{ ...reveal().style, ...s.sectionLabel }}>Everything you need</div>
          <h2 {...reveal(1)} style={{ ...s.sectionTitle, margin: "0 auto 16px" }}>Built for the way you actually work</h2>
          <p {...reveal(2)} style={{ ...s.sectionSub, margin: "0 auto 52px" }}>Every feature is designed around the real daily flow of running an early education school — not generic software retrofitted for childcare.</p>
        </div>
        <div style={s.featuresGrid}>
          {features.map((f, i) => (
            <div key={f.title} {...reveal(i)} style={s.featureCard}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = `0 16px 48px ${T.navy}14`; e.currentTarget.style.borderColor = T.primaryLight; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; e.currentTarget.style.borderColor = T.border; }}>
              <div style={s.featureIcon(f.bg)}>{f.icon}</div>
              <h3 style={s.featureTitle}>{f.title}</h3>
              <p style={s.featureDesc}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" style={s.section(T.bg)}>
        <div style={{ textAlign: "center" }}>
          <div {...reveal()} style={{ ...reveal().style, ...s.sectionLabel }}>Simple setup</div>
          <h2 {...reveal(1)} style={{ ...s.sectionTitle, margin: "0 auto" }}>Up and running in minutes</h2>
        </div>
        <div style={s.stepsGrid}>
          {[
            { n: "1", title: "Create your school", desc: "Sign up, enter your school name and details, and you're in. No IT setup, no installs — just a browser." },
            { n: "2", title: "Add classrooms & staff", desc: "Set up your classrooms, invite your teachers, and enroll your students. Import from a spreadsheet or add them one by one." },
            { n: "3", title: "Go live", desc: "Start taking attendance, logging daily activities, and messaging parents from day one. Your whole school in your pocket." },
          ].map((step, i) => (
            <div key={step.n} {...reveal(i)} style={s.step}>
              <div style={s.stepNum}>{step.n}</div>
              <h3 style={s.stepTitle}>{step.title}</h3>
              <p style={s.stepDesc}>{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" style={s.section("white")}>
        <div style={{ textAlign: "center" }}>
          <div {...reveal()} style={{ ...reveal().style, ...s.sectionLabel }}>Pricing</div>
          <h2 {...reveal(1)} style={{ ...s.sectionTitle, margin: "0 auto 16px" }}>Simple, honest pricing</h2>
          <p {...reveal(2)} style={{ ...s.sectionSub, margin: "0 auto" }}>One plan, everything included. No per-user fees, no feature tiers, no surprises.</p>
        </div>
        <div {...reveal(3)} style={s.pricingCard}>
          <div style={s.pricingBar} />
          <div style={s.pricingBadge}>SMP Pro</div>
          <div style={s.pricingName}>Everything your school needs</div>
          <div style={s.pricingPrice}><sup style={{ fontSize: 26, verticalAlign: "top", marginTop: 12 }}>$</sup>19<sub style={{ fontSize: 17, fontWeight: 600, color: T.textMuted }}>/month</sub></div>
          <p style={s.pricingNote}>Per school · Unlimited staff & students</p>
          <ul style={s.featureList}>
            {pricingFeatures.map(f => (
              <li key={f} style={s.featureItem}><div style={s.check}>✓</div>{f}</li>
            ))}
          </ul>
          <button style={{ ...s.btnHero, width: "100%", display: "block" }} onClick={onSignUp}>Start 30-Day Free Trial →</button>
          <p style={{ marginTop: 16, fontSize: 13, color: T.textMuted }}>Credit card required. <strong style={{ color: T.green }}>Free for 30 days</strong>, then $19/month. Cancel anytime.</p>
        </div>
      </section>

      {/* TESTIMONIALS — removed until real quotes are available */}

      {/* FAQ */}
      <section id="faq" style={s.section("white")}>
        <div style={{ textAlign: "center" }}>
          <div {...reveal()} style={{ ...reveal().style, ...s.sectionLabel }}>FAQ</div>
          <h2 {...reveal(1)} style={{ ...s.sectionTitle, margin: "0 auto" }}>Common questions</h2>
        </div>
        <div style={s.faqGrid}>
          {faqs.map((f, i) => (
            <div key={f.q} {...reveal(i)} style={s.faqItem}>
              <div style={s.faqQ}>{f.q}</div>
              <div style={s.faqA}>{f.a}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={s.cta}>
        <div {...reveal()} style={{ ...reveal().style, ...s.sectionLabel }}>Get started today</div>
        <h2 {...reveal(1)} style={{ ...s.sectionTitle, margin: "0 auto 16px" }}>Your school deserves better tools</h2>
        <p {...reveal(2)} style={{ fontSize: 18, color: T.textMuted, marginBottom: 40 }}>Join schools already running smoother with ShrewCare.</p>
        <div {...reveal(3)}>
          <button style={s.btnHero} onClick={()=>setShowWaitlist(true)}>Request Early Access →</button>
          <p style={{ marginTop: 16, fontSize: 13, color: T.textMuted }}>Early access · No credit card required</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={s.footer}>
        <div style={s.footerTop}>
          <div>
            <div style={{ ...s.navLogo, marginBottom: 12 }}>
              <div style={s.navLogoIcon}>S</div>
              <span style={{ color: "white" }}>ShrewCare</span>
            </div>
            <p style={{ fontSize: 14, color: "rgba(255,255,255,0.4)", maxWidth: 280, lineHeight: 1.6 }}>The all-in-one school management platform built for early education.</p>
          </div>
          <div>
            <div style={s.footerHead}>Product</div>
            {["Features", "How it works", "Pricing"].map(l => <button key={l} style={s.footerLink} onClick={() => document.getElementById(l.toLowerCase().replaceAll(" ", "-"))?.scrollIntoView({ behavior: "smooth" })}>{l}</button>)}
          </div>
          <div>
            <div style={s.footerHead}>Account</div>
            <button style={s.footerLink} onClick={onSignUp}>Sign Up</button>
            <button style={s.footerLink} onClick={onSignIn}>Sign In</button>
          </div>
          <div>
            <div style={s.footerHead}>Legal</div>
            <button style={s.footerLink} onClick={() => setLegalPage("privacy")}>Privacy Policy</button>
            <button style={s.footerLink} onClick={() => setLegalPage("terms")}>Terms of Service</button>
          </div>
        </div>
        <div style={s.footerBottom}>
          <p style={s.footerText}>© {new Date().getFullYear()} ShrewCare. All rights reserved.</p>
          <p style={s.footerText}>Built with care for early educators.</p>
        </div>
      </footer>

      {/* ── Waitlist Modal ── */}
      {showWaitlist && (
        <div style={{ position:"fixed",inset:0,zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:20 }}>
          {/* Backdrop */}
          <div style={{ position:"absolute",inset:0,background:"rgba(0,0,0,0.5)",backdropFilter:"blur(6px)" }} onClick={()=>{ setShowWaitlist(false); setWaitlistStatus("idle"); setWaitlistEmail(""); setWaitlistSchool(""); }} />
          {/* Modal */}
          <div style={{ position:"relative",background:"#fff",borderRadius:24,padding:40,maxWidth:480,width:"100%",boxShadow:"0 24px 80px rgba(0,0,0,0.2)" }}>
            {waitlistStatus === "success" ? (
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:48,marginBottom:16 }}>🎉</div>
                <h3 style={{ fontSize:24,fontWeight:800,color:"#2D1B4E",marginBottom:12 }}>You're on the list!</h3>
                <p style={{ fontSize:15,color:"#6B7280",lineHeight:1.6,marginBottom:24 }}>
                  We'll reach out to <strong>{waitlistEmail}</strong> when early access opens for <strong>{waitlistSchool}</strong>. We're onboarding schools in order of sign-up.
                </p>
                <button onClick={()=>{ setShowWaitlist(false); setWaitlistStatus("idle"); setWaitlistEmail(""); setWaitlistSchool(""); }} style={{ background:T.primary,color:"#fff",border:"none",borderRadius:14,padding:"14px 32px",fontSize:15,fontWeight:700,cursor:"pointer" }}>
                  Done
                </button>
              </div>
            ) : (
              <>
                <button onClick={()=>{ setShowWaitlist(false); setWaitlistStatus("idle"); }} style={{ position:"absolute",top:16,right:16,background:"none",border:"none",cursor:"pointer",fontSize:20,color:"#9CA3AF" }}>✕</button>
                <div style={{ display:"inline-flex",alignItems:"center",gap:8,background:T.primaryPale,border:`1px solid ${T.primary}44`,borderRadius:100,padding:"6px 14px",fontSize:12,fontWeight:700,color:T.primary,marginBottom:20 }}>
                  <div style={{ width:6,height:6,borderRadius:"50%",background:T.green }} />
                  Limited early access spots
                </div>
                <h3 style={{ fontSize:24,fontWeight:800,color:"#2D1B4E",marginBottom:8 }}>Request Early Access</h3>
                <p style={{ fontSize:14,color:"#6B7280",marginBottom:24,lineHeight:1.6 }}>
                  Join schools already on the waitlist. We're onboarding a limited number of schools and will reach out to confirm your spot.
                </p>
                <div style={{ marginBottom:16 }}>
                  <label style={{ fontSize:13,fontWeight:600,color:"#1A1A2E",display:"block",marginBottom:6 }}>School Name</label>
                  <input
                    type="text"
                    placeholder="Sunshine Academy"
                    value={waitlistSchool}
                    onChange={e=>setWaitlistSchool(e.target.value)}
                    style={{ width:"100%",padding:"13px 16px",borderRadius:12,border:"1.5px solid rgba(0,0,0,0.1)",fontSize:15,fontFamily:"inherit",outline:"none",boxSizing:"border-box" }}
                  />
                </div>
                <div style={{ marginBottom:24 }}>
                  <label style={{ fontSize:13,fontWeight:600,color:"#1A1A2E",display:"block",marginBottom:6 }}>Email Address</label>
                  <input
                    type="email"
                    placeholder="you@school.com"
                    value={waitlistEmail}
                    onChange={e=>setWaitlistEmail(e.target.value)}
                    style={{ width:"100%",padding:"13px 16px",borderRadius:12,border:"1.5px solid rgba(0,0,0,0.1)",fontSize:15,fontFamily:"inherit",outline:"none",boxSizing:"border-box" }}
                  />
                </div>
                {waitlistStatus === "error" && (
                  <p style={{ color:"#EF5350",fontSize:13,marginBottom:16 }}>Something went wrong. Please try again.</p>
                )}
                <button
                  onClick={handleWaitlistSubmit}
                  disabled={waitlistStatus==="loading"||!waitlistEmail.trim()||!waitlistSchool.trim()}
                  style={{ width:"100%",background:T.primary,color:"#fff",border:"none",borderRadius:14,padding:"15px",fontSize:16,fontWeight:700,cursor:"pointer",opacity:(waitlistStatus==="loading"||!waitlistEmail.trim()||!waitlistSchool.trim())?0.6:1 }}
                >
                  {waitlistStatus==="loading" ? "Submitting..." : "Request Early Access →"}
                </button>
                <p style={{ textAlign:"center",fontSize:12,color:"#9CA3AF",marginTop:14 }}>No credit card required. We'll reach out to confirm your spot.</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
